import "./Accounts.scss";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonLoading,
  IonModal,
  IonPage,
  IonText,
} from "@ionic/react";
import React, { useState } from "react";
import { addOutline, refreshOutline } from "ionicons/icons";

import { createRequestHistory } from "../../api/requestHistory";

import deleteIcon from "../../assets/svg/delete.svg";
import { getUserByIdentity, userRemovePushToken } from "../../api/user";
import {
  ShowToastErrorMessage,
  ShowToastSuccessMessage,
} from "../../utils/helpers/ToastMessage";
import { useTracked } from "../../state";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import { getAccountIcon } from "../../utils/helpers/AccountIcon";
import editIcon from "../../assets/svg/accountIcons/edit.svg";
import deleteAccountIcon from "../../assets/svg/x-circle.svg";
import handIcon from "../../assets/svg/hand.svg";

import moment from "moment";
import p from "../../../package.json";
import RecoverAccountModal from "../../components/RecoverAccountModal";
import { formatDeletedAccountEmail } from "../../utils/helpers/String";
import { identityService } from "../../services/vault";
import { loadAuthMode } from "../../context/vault";

interface Props {
  user: any;
  accounts: any;
  dispatch: any;
  deletedAccounts: any;
  state: any;
}

const Accounts: React.FC<Props> = React.memo(
  ({ user, accounts = [], deletedAccounts = [], dispatch, state }) => {
    const history = useHistory();
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedEmail, setSelectedEmail] = useState<string>("");
    const [selectedAccount, setSelectedAccount] = useState<any>(null);
    const [isOpenDeleteAccountModal, setIsOpenDeleteAccountModal] =
      useState<boolean>(false);
    const [isOpenRecoverAccountModal, setIsOpenRecoverAccountModal] =
      useState<boolean>(false);

    const handleSwitchAccount = async (identity: any) => {
      try {
        if (user.identity === identity) {
          history.replace("/issuers");
        } else {
          setLoading(true);

          const user: any = await getUserByIdentity(identity.toLowerCase());

          if (user?.data?.id) {
            const { aliases, issuers } = user.data;

            dispatch({ user: user.data, type: "setUser" });
            dispatch({ lastUser: user.data, type: "setLastUser" });

            if (issuers) {
              dispatch({ issuers, type: "setIssuers" });
            }

            if (aliases) {
              dispatch({ aliases, type: "setAliases" });
            }

            dispatch({ identity, type: "setIdentity" });

            history.replace("/issuers");
          } else {
            ShowToastErrorMessage("Switch account failed");
          }

          setLoading(false);
        }
      } catch (e) {
        console.log(e);
        setLoading(false);
        ShowToastErrorMessage("Switch account failed");
      }
    };

    const handleDeleteAccount = async (account: any) => {
      ShowToastSuccessMessage(
        `Identity deleted successfully. If you want to return it, press the "Recover button"`,
        {
          autoClose: 8000,
        }
      );
      dispatch({ type: "setDeletedAccount", deletedAccount: account });
      await createRequestHistory({
        user,
        name: " ",
        type: "delete_account",
        alias: selectedAccount.email,
        status: "DELETED",
        appId: 1, // verifier app ID -> always on verifier app
        createdId: user.id,
      });

      if (account?.identity?.toLowerCase() === state.identity?.toLowerCase()) {
        handleLogout();
      }

      setIsOpenDeleteAccountModal(false);
    };

    const handleLogout = async () => {
      userRemovePushToken(state.identity.toLowerCase(), state.pushToken);
      await identityService.logout();
      await updateVaultAuth();
  
      if (state.mqm) {
        await state.mqm.pubnub.publish({
          channel: state.identity.toLowerCase(),
          meta: {
            type: "logout",
          },
          message: { appId: process.env.REACT_APP_APP_ID },
        });
      }
      
      history.replace("/", { direction: "none" });
    };
  
    const updateVaultAuth = async () => {
      const { authMode, biometricType, hasSession, biometricsSupported } =
        await loadAuthMode();
  
      dispatch({
        type: "setVaultAuth",
        authMode,
        biometricType,
        hasSession,
        biometricsSupported,
      });
    };

    return (
      <>
        <IonPage className="accounts-page">
          {/* <IonHeader className="ion-no-border" mode="ios">
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Accounts</IonTitle>
          <LockButtonIcon />
        </IonToolbar>
      </IonHeader> */}

          <IonContent>
            <div className="accounts-container">
              <IonText className="welcome-content">
                <h5>
                  Hello, {user?.username ? user.username : user?.email}
                  <IonIcon icon={handIcon} />
                </h5>
                <h1>Welcome to Verifier!</h1>
              </IonText>

              <IonText className="accounts-label">
                <h3>MY ACCOUNTS</h3>
              </IonText>
              <IonList mode="ios" lines="none">
                {accounts.map((account: any) => {
                  const {
                    id,
                    accountLabel,
                    accountIcon,
                    accountColor,
                    email,
                    identity,
                  } = account;
                  const icon: any = getAccountIcon(accountIcon);
                  const style: any = {
                    background: accountColor || "#53A6DE",
                  };

                  return (
                    <IonItemSliding key={id}>
                      <IonItem
                        lines="none"
                        className={clsx({
                          active: identity === user.identity,
                        })}
                        onClick={() => {
                          handleSwitchAccount(identity);
                        }}
                      >
                        <div
                          slot="start"
                          className="account-icon"
                          style={style}
                        >
                          <img src={icon} alt="" />
                        </div>

                        <IonLabel className="sc-IonLabel-ios-h sc-IonLabel-ios-s ios hydrated">
                          <h3>{accountLabel || "Untitled"}</h3>
                          <p>{email}</p>
                        </IonLabel>
                      </IonItem>

                      <IonItemOptions side="end">
                        <IonItemOption
                          onClick={() => {
                            history.push(`/add-account/${identity}`);
                          }}
                        >
                          <div className="sliding-btn-container">
                            <IonIcon icon={editIcon} />
                            <span>Edit</span>
                          </div>
                        </IonItemOption>

                        {identity !== user.identity && (
                          <IonItemOption
                            onClick={() => {
                              setSelectedAccount(account);
                              setIsOpenDeleteAccountModal(true);
                            }}
                          >
                            <div className="sliding-btn-container">
                              <IonIcon icon={deleteIcon} />
                              <span>Delete</span>
                            </div>
                          </IonItemOption>
                        )}
                      </IonItemOptions>
                    </IonItemSliding>
                  );
                })}

                <IonItem
                  lines="none"
                  className="add-account"
                  routerDirection="forward"
                  routerLink="/add-account"
                  detail={true}
                >
                  <IonIcon icon={addOutline} slot="start" />
                  <IonLabel className="sc-IonLabel-ios-h sc-IonLabel-ios-s ios hydrated">
                    <h3>Add account</h3>
                  </IonLabel>
                </IonItem>

                <IonItem
                  lines="none"
                  className="recover-account"
                  detail={true}
                  onClick={() => {
                    setSelectedEmail("");
                    setIsOpenRecoverAccountModal(true);
                  }}
                >
                  <IonIcon icon={refreshOutline} slot="start" />
                  <IonLabel className="sc-IonLabel-ios-h sc-IonLabel-ios-s ios hydrated">
                    <h3>Recover</h3>
                  </IonLabel>
                </IonItem>

                {accounts.length === 1 && (
                  <IonItem
                    lines="none"
                    className="recover-account"
                    detail={true}
                    onClick={() => {
                      setSelectedAccount(accounts[0]);
                      setIsOpenDeleteAccountModal(true);
                    }}
                  >
                    <IonIcon icon={deleteIcon} slot="start" />
                    <IonLabel className="sc-IonLabel-ios-h sc-IonLabel-ios-s ios hydrated">
                      <h3>Delete Account</h3>
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>

              {deletedAccounts.length > 0 && (
                <>
                  <IonText className="accounts-label">
                    <h3>DELETED ACCOUNTS</h3>
                  </IonText>
                  <IonList mode="ios" lines="none">
                    {deletedAccounts.map((account: any) => {
                      const {
                        id,
                        accountLabel,
                        accountIcon,
                        accountColor,
                        email,
                      } = account;
                      const icon: any = getAccountIcon(accountIcon);
                      const style: any = {
                        background: accountColor || "#53A6DE",
                      };

                      return (
                        <IonItemSliding key={id}>
                          <IonItem lines="none" className="deleted-account">
                            <div
                              slot="start"
                              className="account-icon"
                              style={style}
                            >
                              <img src={icon} alt="" />
                            </div>

                            <IonLabel className="sc-IonLabel-ios-h sc-IonLabel-ios-s ios hydrated">
                              <h3>{accountLabel || "Untitled"}</h3>
                              <p>{formatDeletedAccountEmail(email)}</p>
                            </IonLabel>
                          </IonItem>

                          <IonItemOptions side="end">
                            <IonItemOption
                              onClick={() => {
                                setSelectedEmail(email);
                                setIsOpenRecoverAccountModal(true);
                              }}
                            >
                              <div className="sliding-btn-container">
                                <IonIcon icon={refreshOutline} />
                                <span>Recover</span>
                              </div>
                            </IonItemOption>
                          </IonItemOptions>
                        </IonItemSliding>
                      );
                    })}
                  </IonList>
                </>
              )}
            </div>

            <IonLoading
              isOpen={loading}
              onDidDismiss={() => setLoading(false)}
              message={"Please wait..."}
            />
          </IonContent>
        </IonPage>

        <IonModal
          isOpen={isOpenDeleteAccountModal}
          className="delete-account-modal"
        >
          <IonContent>
            <div className="delete-account-modal-container">
              <div className="delete-account-modal-content">
                <IonIcon icon={deleteAccountIcon} className="modal-icon" />
                <IonText className="title">Delete identity?</IonText>
                <p>
                  Are you sure you want to permanently delete{" "}
                  <span className="selected-account">
                    {selectedAccount?.email}
                  </span>{" "}
                  from this device?
                </p>
                <p className="ion-no-margin">
                  Your identity will continue to exists in ID++ and can be restored
                  at a later time
                </p>

                <IonButton
                  color="danger"
                  expand="full"
                  shape="round"
                  className="delete-account-btn"
                  onClick={() => {
                    handleDeleteAccount(selectedAccount);
                  }}
                >
                  Delete
                </IonButton>

                <IonButton
                  fill="outline"
                  expand="full"
                  shape="round"
                  className="cancel-delete-account-btn"
                  onClick={() => {
                    setIsOpenDeleteAccountModal(false);
                  }}
                >
                  Cancel
                </IonButton>

                <p className="footer-text">
                  © {moment().format("YYYY")} Abaxx Technologies | ver{" "}
                  {p.version}
                </p>
              </div>
            </div>
          </IonContent>
        </IonModal>
        <RecoverAccountModal
          isOpen={isOpenRecoverAccountModal}
          setIsOpen={setIsOpenRecoverAccountModal}
          selectedEmail={selectedEmail}
          setSelectedEmail={setSelectedEmail}
        />
      </>
    );
  }
);

const MemoizedAliases = () => {
  const [state, dispatch] = useTracked();
  const { user, accounts, deletedAccounts } = state;
  return (
    <Accounts
      accounts={accounts}
      user={user}
      state={state}
      dispatch={dispatch}
      deletedAccounts={deletedAccounts}
    />
  );
};

export default MemoizedAliases;
